import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import LazyImage from './LazyImage.tsx';
import { useLocation, useNavigate } from 'react-router-dom';

const HeaderContainer = styled.header<{ isScrolled: boolean; $activeSection: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 1rem 0;
  transition: all 0.3s ease;
  /* Fondo sólido (sin imagen) */
  background-color: rgba(220, 215, 209, 0.3);
  backdrop-filter: ${props => props.isScrolled ? 'blur(2px)' : 'none'};
  border-bottom: ${props => props.isScrolled 
    ? '1px solid rgba(64, 224, 208, 0.2)' 
    : '1px solid transparent'};

  /* Color del texto del menú según sección visible */
  --menu-link-color: ${props =>
    (props.$activeSection === 'menu-catalog' || props.$activeSection === 'contacto')
      ? 'var(--navy-blue)'
      : '#e3e3dd'};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  justify-content: flex-end;
`;

const LogoImageWrap = styled.div`
  width: 180px;
  height: 58px;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 140px;
    height: 46px;
  }
`;

const Logo = styled.a`
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    transform: none;
  }
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const NavMenu = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  gap: 2rem;
  align-items: center;
  margin: 0;
  padding: 0;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: rgba(220, 215, 209, 0.3);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: 2rem 0;
    gap: 1rem;
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    border-bottom: 1px solid rgba(64, 224, 208, 0.2);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    z-index: 9998;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }
`;

const NavItem = styled.li`
  position: relative;
  
  a {
    color: var(--menu-link-color);
    font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 0.65rem 1.05rem;
    border-radius: 999px;
    position: relative;
    overflow: hidden;
    display: block;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    z-index: 1;
    /* Look más formal: sin sombras ni bordes en el texto */
    text-shadow: none;
    -webkit-text-stroke: 0px transparent;
    /* Por defecto transparente (sin borde) */
    background: transparent;
    border: 1px solid transparent;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: var(--navy-blue);
      transition: left 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      z-index: -1;
      border-radius: 999px;
    }
    
    &:hover::before {
      left: 0;
    }
    
    &:hover {
      color: #e3e3dd;
      transform: none;
      text-decoration: none;
      text-shadow: none;
    }

    &:focus,
    &:focus-visible {
      text-decoration: none;
      outline: none;
    }
    
    @media (max-width: 768px) {
      padding: 1rem 2rem;
      font-size: 1.1rem;
      width: 200px;
      text-align: center;
      
      &:hover {
        transform: none;
      }
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
  
  a {
    color: var(--white);
    font-size: 1.2rem;
    width: 40px;
    height: 40px;
    padding: 0;
    display: grid;
    place-items: center;
    border-radius: 50%;
    /* Fondo azul de marca al 75% (solo en la barra de menú) */
    background: rgba(1, 74, 144, 0.75);
    transition: all 0.3s ease;
    flex-shrink: 0;
    
    &:hover {
      background: var(--brand-gold);
      color: var(--navy-blue);
      transform: translateY(-1px) scale(1.08);
    }
  }
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    
    a {
      font-size: 1rem;
      width: 34px;
      height: 34px;
      background: rgba(1, 74, 144, 0.75);
    }
  }
`;

const MenuToggle = styled.button`
  display: none;
  flex-direction: column;
  gap: 4px;
  background: transparent;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  position: relative;
  z-index: 10001;
  
  span {
    width: 25px;
    height: 3px;
    background: var(--primary-turquoise);
    border-radius: 2px;
    transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    transform-origin: center;
  }
  
  &.active span:nth-child(1) {
    transform: rotate(45deg) translate(6px, 6px);
    background: var(--white);
  }
  
  &.active span:nth-child(2) {
    opacity: 0;
    transform: scale(0);
  }
  
  &.active span:nth-child(3) {
    transform: rotate(-45deg) translate(6px, -6px);
    background: var(--white);
  }
  
  &:hover span {
    background: var(--white);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const Header: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('inicio');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Animate header on mount with improved timing
    const tl = gsap.timeline();
    
    tl.fromTo('.header-logo', 
      { opacity: 0, x: -50, scale: 0.8 },
      { opacity: 1, x: 0, scale: 1, duration: 1.2, ease: 'back.out(1.7)' }
    )
    .fromTo('.nav-item', 
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out' },
      '-=0.8'
    )
    .fromTo('.social-links a', 
      { opacity: 0, scale: 0, rotation: -180 },
      { opacity: 1, scale: 1, rotation: 0, duration: 0.6, stagger: 0.1, ease: 'back.out(1.7)' },
      '-=0.4'
    )
    .fromTo('.menu-toggle', 
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)' },
      '-=0.3'
    );
  }, []);
  
  // Add GSAP animation for mobile menu toggle
  useEffect(() => {
    if (isMenuOpen) {
      gsap.fromTo('.nav-item', 
        { opacity: 0, y: -20, scale: 0.8 },
        { opacity: 1, y: 0, scale: 1, duration: 0.4, stagger: 0.1, ease: 'back.out(1.7)', delay: 0.1 }
      );
    }
  }, [isMenuOpen]);

  // Scrollspy: detecta la sección visible para cambiar colores del menú
  useEffect(() => {
    // Solo aplica en home, donde existen las secciones con IDs
    if (location.pathname !== '/') return;

    const sectionIds = ['inicio', 'menu-catalog', 'locales', 'contacto'];
    const getElements = () =>
      sectionIds
        .map((id) => document.getElementById(id))
        .filter(Boolean) as HTMLElement[];

    let raf = 0;
    const compute = () => {
      const elements = getElements();
      if (elements.length === 0) return;

      // Punto de referencia: ~1/3 del alto de la pantalla (debajo del header fijo)
      const probeY = Math.min(window.innerHeight * 0.33, 260);

      const current = elements.find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.top <= probeY && rect.bottom >= probeY;
      });

      if (current?.id) setActiveSection(current.id);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    // Inicial
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
      setActiveSection(sectionId);
      return;
    }

    // Si estamos fuera del home (ej: /menu/:id), navega al home y scrollea allí
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      setIsMenuOpen(false);
      return;
    }

    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer isScrolled={isScrolled} $activeSection={activeSection}>
      <Nav>
        <Logo
          className="header-logo"
          href="#inicio"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('inicio');
          }}
          aria-label="Ir al inicio"
        >
          <LogoImageWrap>
            <LazyImage 
              src={`${process.env.PUBLIC_URL}/logo.png`}
              fallbackSrc={`${process.env.PUBLIC_URL}/logo.jpg`}
              alt="" 
              width="100%"
              height="100%"
              objectFit="contain"
              borderRadius="10px"
              placeholder="fas fa-fish"
            />
          </LogoImageWrap>
        </Logo>
        
        <NavMenu isOpen={isMenuOpen}>
          <NavItem className="nav-item">
            <a
              href="#inicio"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('inicio');
              }}
            >
              Inicio
            </a>
          </NavItem>
          <NavItem className="nav-item">
            <a
              href="#menu-catalog"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('menu-catalog');
              }}
            >
              Carta
            </a>
          </NavItem>
          <NavItem className="nav-item">
            <a
              href="#locales"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('locales');
              }}
            >
              Locales
            </a>
          </NavItem>
          <NavItem className="nav-item">
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contacto');
              }}
            >
              Contacto
            </a>
          </NavItem>
        </NavMenu>

        <RightGroup>
          <SocialLinks className="social-links">
            <a href="https://web.facebook.com/pepetiburon01/" target="_blank" rel="noopener noreferrer" aria-label="Facebook Pepe Tiburón">
              <i className="fab fa-facebook-f"></i>
            </a>
            <a href="https://instagram.com/pepe.tiburon" target="_blank" rel="noopener noreferrer" aria-label="Instagram Pepe Tiburón">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="https://tiktok.com/@cevicheriapepe_tiburon" target="_blank" rel="noopener noreferrer" aria-label="TikTok Pepe Tiburón">
              <i className="fab fa-tiktok"></i>
            </a>
          </SocialLinks>

          <MenuToggle 
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Abrir/cerrar menú"
          >
            <span></span>
            <span></span>
            <span></span>
          </MenuToggle>
        </RightGroup>
      </Nav>
    </HeaderContainer>
  );
});  // Closing parenthesis for React.memo

export default Header;
